import React, { useState, useCallback, useEffect, useRef } from "react";
import {
    FaRobot,
    FaSatelliteDish,
    FaPaperPlane,
    FaDice,
    FaSync,
    FaCheckCircle,
    FaTimesCircle,
    FaExclamationTriangle,
    FaCopy,
    FaPlay,
    FaStop,
    FaTrash,
    FaInfoCircle,
} from "react-icons/fa";
import { useRadixConnect } from "../../../radix_connect";
import {
    RADIX_API_GATEWAY_URL,
    CANDY_MACHINE_COMPONENT_ADDRESS,
    CANDY_MACHINE_BOT_BADGE_ADDRESS,
    DCKSLAP_RESOURCE_ADDRESS,
} from "../../../constants";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import "./TestTransactionPage.css";

const truncateAddress = (addr) => {
    if (!addr || addr.length < 20) return addr || "";
    return `${addr.slice(0, 16)}...${addr.slice(-8)}`;
};

const formatTimestamp = (ts) => {
    try {
        const d = new Date(ts);
        return d.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return "—";
    }
};

/**
 * TestTransactionPage — Bot Simulator
 * 
 * Monitors DckslapDepositedEvent events and provides a form to manually
 * trigger the bot's send_candies method for testing purposes.
 */
const TestTransactionPage = () => {
    const { rdt, selectedAccount } = useRadixConnect();

    // Event monitor state
    const [events, setEvents] = useState([]);
    const [isMonitoring, setIsMonitoring] = useState(false);
    const [monitorError, setMonitorError] = useState(null);
    const monitorIntervalRef = useRef(null);
    const monitorStartTimeRef = useRef(null);
    const [hasBotBadge, setHasBotBadge] = useState(null);

    // Send candies form state
    const [targetAccount, setTargetAccount] = useState("");
    const [choice, setChoice] = useState("");
    const [randomNumber, setRandomNumber] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    // Transaction history
    const [txHistory, setTxHistory] = useState([]);

    // Check if connected wallet has the bot badge
    const checkBotBadge = useCallback(async () => {
        if (!selectedAccount?.address) {
            setHasBotBadge(null);
            return;
        }

        try {
            let cursor = null;
            let found = false;

            do {
                const requestBody = { address: selectedAccount.address };
                if (cursor) requestBody.cursor = cursor;

                const response = await fetch(
                    `${RADIX_API_GATEWAY_URL}/state/entity/page/fungibles/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(requestBody),
                    }
                );

                if (!response.ok) break;

                const data = await response.json();
                const items = data.items || [];

                for (const item of items) {
                    if (item.resource_address === CANDY_MACHINE_BOT_BADGE_ADDRESS) {
                        const balance = Number.parseFloat(item.amount) || 0;
                        if (balance >= 1) {
                            found = true;
                            break;
                        }
                    }
                }

                cursor = data.next_cursor || null;
            } while (cursor && !found);

            setHasBotBadge(found);
        } catch (err) {
            console.error("Error checking bot badge:", err);
            setHasBotBadge(false);
        }
    }, [selectedAccount]);

    useEffect(() => {
        checkBotBadge();
    }, [checkBotBadge]);

    // Poll for DckslapDepositedEvent events
    const pollEvents = useCallback(async () => {
        try {
            const fromTimestamp = monitorStartTimeRef.current || new Date().toUTCString();

            const response = await fetch(
                `${RADIX_API_GATEWAY_URL}/stream/transactions`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        limit_per_page: 30,
                        from_ledger_state: { timestamp: fromTimestamp },
                        opt_ins: {
                            receipt_output: false,
                            detailed_events: true,
                            manifest_instructions: true,
                        },
                        affected_global_entities_filter: [CANDY_MACHINE_COMPONENT_ADDRESS],
                        manifest_resources_filter: [DCKSLAP_RESOURCE_ADDRESS],
                        transaction_status_filter: "Success",
                    }),
                }
            );

            if (!response.ok) {
                setMonitorError("Gateway API error");
                return;
            }

            const data = await response.json();
            setMonitorError(null);

            if (!data.items || data.items.length === 0) return;

            const newEvents = [];

            for (const tx of data.items) {
                if (tx.receipt?.status !== "CommittedSuccess") continue;
                if (!tx.receipt?.detailed_events) continue;

                for (const event of tx.receipt.detailed_events) {
                    if (
                        event.identifier?.blueprint === "CandyDispenser" &&
                        event.identifier?.event === "DckslapDepositedEvent"
                    ) {
                        const fields = event.payload?.programmatic_json?.fields;
                        if (!fields) continue;

                        const accountField = fields.find((f) => f.field_name === "account");
                        const choiceField = fields.find((f) => f.field_name === "choice");

                        newEvents.push({
                            id: `${tx.intent_hash}_${event.identifier?.event}`,
                            intentHash: tx.intent_hash,
                            timestamp: tx.confirmed_at || tx.round_timestamp,
                            account: accountField?.value || "Unknown",
                            choice: choiceField?.value || "—",
                            responded: false,
                        });
                    }
                }
            }

            if (newEvents.length > 0) {
                setEvents((prev) => {
                    const existingIds = new Set(prev.map((e) => e.id));
                    const unique = newEvents.filter((e) => !existingIds.has(e.id));
                    return [...unique, ...prev].slice(0, 50);
                });
            }
        } catch (err) {
            console.error("Error polling events:", err);
            setMonitorError(err.message);
        }
    }, []);

    // Start/stop monitoring
    const startMonitoring = useCallback(() => {
        monitorStartTimeRef.current = new Date().toUTCString();
        setIsMonitoring(true);
        setMonitorError(null);

        // Poll immediately
        pollEvents();

        // Then poll every 3 seconds
        monitorIntervalRef.current = setInterval(pollEvents, 3000);
    }, [pollEvents]);

    const stopMonitoring = useCallback(() => {
        setIsMonitoring(false);
        if (monitorIntervalRef.current) {
            clearInterval(monitorIntervalRef.current);
            monitorIntervalRef.current = null;
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
            }
        };
    }, []);

    // Auto-fill form from event
    const handleRespondToEvent = useCallback((event) => {
        setTargetAccount(event.account);
        setChoice(event.choice);
        setRandomNumber(Math.floor(Math.random() * 1000000).toString());

        // Mark event as responded
        setEvents((prev) =>
            prev.map((e) =>
                e.id === event.id ? { ...e, responded: true } : e
            )
        );
    }, []);

    // Generate random number
    const handleGenerateRandom = useCallback(() => {
        setRandomNumber(Math.floor(Math.random() * 1000000).toString());
    }, []);

    // Copy address to clipboard
    const handleCopy = useCallback(async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // Fallback
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }
    }, []);

    // Execute send_candies transaction
    const handleSendCandies = useCallback(async () => {
        if (!rdt || !selectedAccount?.address) {
            setSendResult({ success: false, error: "No wallet connected" });
            return;
        }

        if (!targetAccount || !choice || !randomNumber) {
            setSendResult({ success: false, error: "All fields are required" });
            return;
        }

        const choiceNum = parseInt(choice, 10);
        const randomNum = parseInt(randomNumber, 10);

        if (isNaN(choiceNum) || choiceNum < 1 || choiceNum > 65535) {
            setSendResult({ success: false, error: "Choice must be between 1-65535" });
            return;
        }

        if (isNaN(randomNum) || randomNum < 0 || randomNum > 999999) {
            setSendResult({ success: false, error: "Random number must be between 0-999999" });
            return;
        }

        setIsSending(true);
        setSendResult(null);

        const manifest = `
CALL_METHOD
    Address("${selectedAccount.address}")
    "create_proof_of_amount"
    Address("${CANDY_MACHINE_BOT_BADGE_ADDRESS}")
    Decimal("1")
;
CALL_METHOD
    Address("${CANDY_MACHINE_COMPONENT_ADDRESS}")
    "send_candies"
    ${randomNum}u32
    Address("${targetAccount}")
    ${choiceNum}u16
;
    `.trim();

        console.log("[BotSimulator] 📤 Sending send_candies manifest:", {
            botAccount: selectedAccount.address,
            targetAccount,
            choice: choiceNum,
            randomNumber: randomNum,
        });

        try {
            const result = await rdt.walletApi.sendTransaction({
                transactionManifest: manifest,
                version: 1,
            });

            if (result.isErr()) {
                const errorMsg = result.error?.message || "Transaction rejected";
                console.error("[BotSimulator] ❌ Transaction failed:", result.error);
                setSendResult({ success: false, error: errorMsg });

                setTxHistory((prev) => [
                    {
                        id: Date.now(),
                        timestamp: new Date().toISOString(),
                        targetAccount,
                        choice: choiceNum,
                        randomNumber: randomNum,
                        success: false,
                        error: errorMsg,
                    },
                    ...prev,
                ].slice(0, 20));
            } else {
                const txHash = result.value?.transactionIntentHash || "pending";
                console.log("[BotSimulator] ✅ Transaction submitted:", txHash);
                setSendResult({ success: true, hash: txHash });

                setTxHistory((prev) => [
                    {
                        id: Date.now(),
                        timestamp: new Date().toISOString(),
                        targetAccount,
                        choice: choiceNum,
                        randomNumber: randomNum,
                        success: true,
                        hash: txHash,
                    },
                    ...prev,
                ].slice(0, 20));
            }
        } catch (err) {
            console.error("[BotSimulator] ❌ Error:", err);
            setSendResult({ success: false, error: err.message });
        } finally {
            setIsSending(false);
        }
    }, [rdt, selectedAccount, targetAccount, choice, randomNumber]);

    // Clear form
    const handleClearForm = useCallback(() => {
        setTargetAccount("");
        setChoice("");
        setRandomNumber("");
        setSendResult(null);
    }, []);

    return (
        <div className="test-transaction-page">
            {/* Header */}
            <div className="test-page-header">
                <div className="header-title-row">
                    <FaRobot className="header-icon" />
                    <h1>Bot Simulator</h1>
                </div>
                <p className="header-subtitle">
                    Monitor deposit events and manually trigger <code>send_candies</code> to test the dispenser bot flow.
                </p>
            </div>

            {/* Bot Badge Warning */}
            {hasBotBadge === false && (
                <div className="badge-warning">
                    <FaExclamationTriangle className="warning-icon" />
                    <div>
                        <strong>Bot Badge Not Found</strong>
                        <p>
                            The connected wallet does not hold the bot badge. <code>send_candies</code> will fail without it.
                        </p>
                        <code className="badge-address">{truncateAddress(CANDY_MACHINE_BOT_BADGE_ADDRESS)}</code>
                    </div>
                </div>
            )}

            {hasBotBadge === true && (
                <div className="badge-ok">
                    <FaCheckCircle className="ok-icon" />
                    <span>Bot badge detected — ready to simulate.</span>
                </div>
            )}

            <div className="test-content">
                {/* LEFT — Event Monitor */}
                <div className="test-monitor-section">
                    <Card className="monitor-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <FaSatelliteDish className="monitor-title-icon" />
                                <span>Event Monitor</span>
                                <div className="monitor-controls">
                                    {isMonitoring ? (
                                        <button
                                            className="monitor-btn stop"
                                            onClick={stopMonitoring}
                                            title="Stop monitoring"
                                        >
                                            <FaStop />
                                            <span>Stop</span>
                                        </button>
                                    ) : (
                                        <button
                                            className="monitor-btn start"
                                            onClick={startMonitoring}
                                            title="Start monitoring"
                                        >
                                            <FaPlay />
                                            <span>Listen</span>
                                        </button>
                                    )}
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {monitorError && (
                                <div className="monitor-error">
                                    <FaTimesCircle />
                                    <span>{monitorError}</span>
                                </div>
                            )}

                            {isMonitoring && (
                                <div className="monitor-live-indicator">
                                    <span className="live-dot" />
                                    <span>Listening for DckslapDepositedEvent...</span>
                                </div>
                            )}

                            {events.length === 0 ? (
                                <div className="monitor-empty">
                                    <FaSatelliteDish className="empty-icon" />
                                    <p>No events detected yet.</p>
                                    <p className="empty-hint">
                                        {isMonitoring
                                            ? "Waiting for deposits on the network..."
                                            : "Click Listen to start monitoring."}
                                    </p>
                                </div>
                            ) : (
                                <div className="events-list">
                                    {events.map((event) => (
                                        <div
                                            key={event.id}
                                            className={`event-row ${event.responded ? "responded" : ""}`}
                                        >
                                            <div className="event-info">
                                                <div className="event-time">
                                                    {formatTimestamp(event.timestamp)}
                                                </div>
                                                <div className="event-account" title={event.account}>
                                                    {truncateAddress(event.account)}
                                                    <button
                                                        className="copy-btn"
                                                        onClick={() => handleCopy(event.account)}
                                                        title="Copy address"
                                                    >
                                                        <FaCopy />
                                                    </button>
                                                </div>
                                                <div className="event-choice">
                                                    Vector: <strong>{event.choice}</strong>
                                                </div>
                                            </div>
                                            <button
                                                className={`respond-btn ${event.responded ? "done" : ""}`}
                                                onClick={() => handleRespondToEvent(event)}
                                                disabled={event.responded}
                                            >
                                                {event.responded ? (
                                                    <>
                                                        <FaCheckCircle />
                                                        <span>Filled</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaPaperPlane />
                                                        <span>Respond</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* RIGHT — Send Candies Form */}
                <div className="test-form-section">
                    <Card className="form-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <FaPaperPlane className="form-title-icon" />
                                <span>Send Candies</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {/* Manifest info */}
                            <div className="manifest-info">
                                <FaInfoCircle />
                                <span>
                                    Calls <code>send_candies</code> on the CandyDispenser component using the bot
                                    badge from the connected wallet.
                                </span>
                            </div>

                            {/* Form fields */}
                            <div className="form-fields">
                                <div className="form-field">
                                    <label htmlFor="target-account">Target Account</label>
                                    <input
                                        id="target-account"
                                        type="text"
                                        placeholder="account_rdx1... or account_tdx_2_..."
                                        value={targetAccount}
                                        onChange={(e) => setTargetAccount(e.target.value)}
                                        disabled={isSending}
                                    />
                                    <span className="field-hint">The account that deposited DCKSLAP</span>
                                </div>

                                <div className="form-row">
                                    <div className="form-field">
                                        <label htmlFor="choice-input">Choice (u16)</label>
                                        <input
                                            id="choice-input"
                                            type="number"
                                            min="1"
                                            max="65535"
                                            placeholder="1-65535"
                                            value={choice}
                                            onChange={(e) => setChoice(e.target.value)}
                                            disabled={isSending}
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label htmlFor="random-input">Random (u32)</label>
                                        <div className="input-with-btn">
                                            <input
                                                id="random-input"
                                                type="number"
                                                min="0"
                                                max="999999"
                                                placeholder="0-999999"
                                                value={randomNumber}
                                                onChange={(e) => setRandomNumber(e.target.value)}
                                                disabled={isSending}
                                            />
                                            <button
                                                className="generate-btn"
                                                onClick={handleGenerateRandom}
                                                disabled={isSending}
                                                title="Generate random number"
                                            >
                                                <FaDice />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="form-actions">
                                <button
                                    className={`send-btn ${isSending ? "sending" : ""}`}
                                    onClick={handleSendCandies}
                                    disabled={isSending || !targetAccount || !choice || !randomNumber}
                                >
                                    {isSending ? (
                                        <>
                                            <FaSync className="spin-icon" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane />
                                            <span>Execute send_candies</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    className="clear-btn"
                                    onClick={handleClearForm}
                                    disabled={isSending}
                                >
                                    <FaTrash />
                                    <span>Clear</span>
                                </button>
                            </div>

                            {/* Result */}
                            {sendResult && (
                                <div className={`send-result ${sendResult.success ? "success" : "error"}`}>
                                    {sendResult.success ? (
                                        <>
                                            <FaCheckCircle className="result-icon" />
                                            <div>
                                                <strong>Transaction Submitted</strong>
                                                <code className="result-hash" title={sendResult.hash}>
                                                    {truncateAddress(sendResult.hash)}
                                                </code>
                                                <button
                                                    className="copy-btn"
                                                    onClick={() => handleCopy(sendResult.hash)}
                                                >
                                                    <FaCopy />
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <FaTimesCircle className="result-icon" />
                                            <div>
                                                <strong>Failed</strong>
                                                <span>{sendResult.error}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Transaction History */}
                    {txHistory.length > 0 && (
                        <Card className="history-card">
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <FaSync className="history-title-icon" />
                                    <span>Transaction History</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="history-list">
                                    {txHistory.map((tx) => (
                                        <div
                                            key={tx.id}
                                            className={`history-row ${tx.success ? "success" : "error"}`}
                                        >
                                            <div className="history-status">
                                                {tx.success ? (
                                                    <FaCheckCircle className="status-ok" />
                                                ) : (
                                                    <FaTimesCircle className="status-fail" />
                                                )}
                                            </div>
                                            <div className="history-info">
                                                <div className="history-meta">
                                                    <span className="history-time">
                                                        {formatTimestamp(tx.timestamp)}
                                                    </span>
                                                    <span className="history-params">
                                                        Choice: {tx.choice} | Random: {tx.randomNumber}
                                                    </span>
                                                </div>
                                                <div className="history-target" title={tx.targetAccount}>
                                                    → {truncateAddress(tx.targetAccount)}
                                                </div>
                                                {tx.hash && (
                                                    <code className="history-hash" title={tx.hash}>
                                                        {truncateAddress(tx.hash)}
                                                    </code>
                                                )}
                                                {tx.error && (
                                                    <span className="history-error">{tx.error}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestTransactionPage;
